import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { LOGOUT } from '@hotjar/webapp/constants/routes/auth';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { tokens, DropdownMenu, Avatar, Button, Divider } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import LOGO from '@hotjar/shared-assets/images/next-logo-hotjar.svg';
import { getUserAvatarUrl } from '@hotjar/insights-common/lib/getUserAvatarUrl';
import { OnboardingProgressBar } from '@hotjar/webapp/features/Activation/OnboardingProgressBar';
import {
  isUrlOnboarding,
  isUrlRegistration,
} from '@hotjar/webapp/features/Activation/OnboardingProgressBar/utils';
import type { usePreservedAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';
import {
  ONBOARDING_STEPS,
  useOnboardingNavigation,
} from '@hotjar/webapp/features/Activation/OnboardingPage/steps/OrganizationSteps/hooks/useOnboardingNavigation';
import { LanguageSelector } from '@hotjar/translations';
import { useCurrentUser } from '@hotjar/user';
import { isAllowed } from '@hotjar/feature-flags';
import {
  Stepper,
  StepperWrapper,
  STEPPER_OFFSET_ONBOARDING,
  STEPPER_OFFSET_REGISTRATION,
} from '@hotjar/webapp/features/Registration/components/Stepper';

const ImageWrapper = styled.div`
  position: relative;
  min-height: 52px;
  background: ${tokens.color.ui_background};
  box-shadow: inset 0px -1px 0px #e4e4e4;
  z-index: 10;
  transition: all 0.2s linear;
`;

const StyledImage = styled.img`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: ${tokens.spacer.x3};
`;

const HeaderGroupRight = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: ${tokens.spacer.x2};
  display: flex;
  gap: ${tokens.spacer.x1};
  align-items: center;
`;

const BackButton = styled(Button)`
  position: absolute;
  left: ${tokens.spacer.x2};
  top: 0;
  bottom: 0;
  margin: auto;
`;

const AvatarButton = styled.button`
  border-radius: 50%;
  border: 0;
  outline: none;
  padding: ${tokens.spacer.x0};
  background-color: ${tokens.color.ui_background};
  display: inline-flex;
  ${tokens.transition('background-color')};

  &:hover {
    background-color: ${tokens.color.bg_hover};
    border-radius: 50%;
    color: ${tokens.color.text_default};
  }

  &:focus {
    ${tokens.focus()};
    border-radius: 50%;
  }
`;

const StyledDivider = styled(Divider)`
  height: ${tokens.spacer.x3};
  margin: 0 ${tokens.spacer.x0};
`;

export interface SignUpHeaderProps {
  user?: {
    email: string | null;
    id: Parameters<typeof usePreservedAnonymousId>[0];
    name: React.ComponentProps<typeof Avatar>['userName'];
  } & Parameters<typeof getUserAvatarUrl>[0];
  className?: string;
}

export const SignUpHeader = ({ user, className }: SignUpHeaderProps): JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: currentUser } = useCurrentUser();
  const { click: trackClick } = useTrack();
  const isOnboardingProgressBarVisible = isUrlOnboarding() || isUrlRegistration();
  const { step, goToStep } = useOnboardingNavigation();
  const shouldUseLocalizationDevMode = isAllowed('localization-dev-mode', null);
  const { t } = useTranslation('activation');

  const onLogoutClick = () => {
    trackClick('Logout', 'Onboarding', {});
    navigate(LOGOUT.path);
  };

  const handleGoBack = () => {
    trackClick('Back button', 'Onboarding', {});

    if (step === ONBOARDING_STEPS.ROLE) {
      goToStep(ONBOARDING_STEPS.ORG_SIZE);
    } else if (step === ONBOARDING_STEPS.ORG_SIZE) {
      goToStep(ONBOARDING_STEPS.ORG_NAME);
    } else if (step === ONBOARDING_STEPS.GOAL) {
      goToStep(ONBOARDING_STEPS.ROLE);
    }
  };

  const onLanguageClick = (uiPosition: string, locale: string) => {
    trackClick(
      'language_selector_insights',
      uiPosition,
      { url_event_was_triggered_on: pathname },
      { locale },
    );
  };

  const isRegistration = isUrlRegistration();
  const isOnboarding = isUrlOnboarding();
  const isOnboardingPersonalization =
    isOnboarding && step !== ONBOARDING_STEPS.ORG_NAME && step !== ONBOARDING_STEPS.ORG_SIZE;

  const stepperLeftOffset = isRegistration
    ? STEPPER_OFFSET_REGISTRATION
    : STEPPER_OFFSET_ONBOARDING;

  return (
    <>
      {isOnboardingProgressBarVisible ? <OnboardingProgressBar /> : null}
      <ImageWrapper id="signup-header" className={className}>
        {[ONBOARDING_STEPS.ORG_SIZE, ONBOARDING_STEPS.ROLE, ONBOARDING_STEPS.GOAL].includes(
          step as ONBOARDING_STEPS,
        ) ? (
          <BackButton icon="chevron_left" onClick={handleGoBack} kind="ghost" size="m">
            {t('onboarding.header.back')}
          </BackButton>
        ) : null}
        <StepperWrapper offsetLeft={stepperLeftOffset}>
          <Stepper
            isRegistration={isRegistration}
            isOnboarding={isOnboarding}
            isOnboardingPersonalization={isOnboardingPersonalization}
          />
        </StepperWrapper>

        <StyledImage id="logo" src={LOGO} alt="Hotjar" data-version="next" />
        <HeaderGroupRight>
          <LanguageSelector
            shouldUseLocalizationDevMode={shouldUseLocalizationDevMode}
            onClick={onLanguageClick}
            userId={currentUser?.id}
          />
          {user?.email ? (
            <>
              <StyledDivider orientation="vertical" />
              <DropdownMenu
                trigger={
                  <AvatarButton>
                    <Avatar src={getUserAvatarUrl(user)} userName={user.name} size="m" />
                  </AvatarButton>
                }
              >
                <DropdownMenu.Item onClick={onLogoutClick} icon="off">
                  {t('onboarding.header.logout')}
                </DropdownMenu.Item>
              </DropdownMenu>
            </>
          ) : null}
        </HeaderGroupRight>
      </ImageWrapper>
    </>
  );
};
